import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ChevronDown, Flag, Hammer, Search } from 'lucide-react';
import { Eyebrow, TealButton } from '../components/home/HomeSections';

const API = process.env.REACT_APP_BACKEND_URL;
const KIND = { diagnostic: [Search, 'Starter check'], practice: [Hammer, 'Practice task'], milestone: [Flag, 'Mini-project'] };

const PathCard = ({ p, open, onToggle, track, setTrack }) => {
  const missions = track === 'pro' && p.pro_missions?.length ? p.pro_missions : p.missions;
  return (
  <div className={`rounded-2xl border bg-white transition-shadow ${open ? 'border-teal-300 shadow-[0_12px_40px_rgba(11,31,77,0.08)]' : 'border-slate-200 hover:shadow-md'}`} data-testid={`learning-path-${p.id}`}>
    <button onClick={onToggle} className="w-full text-left p-6 flex items-start gap-4" data-testid={`learning-path-toggle-${p.id}`}>
      <div className="flex-1">
        <p className="font-outfit font-semibold text-lg text-[#0b1f4d]">{p.title}</p>
        <p className="text-sm text-slate-500 mt-1">{p.job_titles.join(' · ')}</p>
        <p className="text-xs text-teal-700 font-semibold mt-3">{p.missions.length} practical missions</p>
      </div>
      <ChevronDown className={`w-5 h-5 text-slate-400 mt-1 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && (
      <div className="px-6 pb-6 grid md:grid-cols-2 gap-6 border-t border-slate-100 pt-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">What employers expect</p>
          <ul className="space-y-2 text-sm text-slate-600">{p.expectations.map((e) => <li key={e} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" />{e}</li>)}</ul>
        </div>
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Your missions</p>
            {p.pro_missions?.length > 0 && (
              <div className="inline-flex bg-slate-100 rounded-full p-0.5 text-xs" data-testid={`learning-track-${p.id}`}>
                {[['fresher', 'Final-year & freshers'], ['pro', 'Experienced']].map(([k, l]) => (
                  <button key={k} onClick={() => setTrack(k)} data-testid={`learning-track-${p.id}-${k}`}
                    className={`px-3 py-1 rounded-full font-semibold transition-colors ${track === k ? 'bg-[#0b1f4d] text-white' : 'text-slate-600'}`}>{l}</button>))}
              </div>)}
          </div>
          <ol className="space-y-2.5 text-sm">{missions.map((m, i) => {
            const [Icon, label] = KIND[m.kind] || KIND.practice;
            return <li key={m.title} className="flex gap-3"><span className="w-6 h-6 rounded-full bg-teal-50 text-teal-700 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span><span><span className="text-[#0b1f4d] font-medium">{m.title}</span><span className="block text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Icon className="w-3 h-3" />{label}{m.level && m.kind === 'practice' ? ` · ${m.level}` : ''}{m.time_estimate ? ` · ${m.time_estimate}` : ''}</span></span></li>;
          })}</ol>
        </div>
      </div>
    )}
  </div>
  );
};

export default function LearningPaths() {
  const [paths, setPaths] = useState([]);
  const [open, setOpen] = useState('');
  const [track, setTrack] = useState('fresher');
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') === 'nontechnical' ? 'nontechnical' : 'technical');
  useEffect(() => { if (params.get('tab')) window.scrollTo(0, 0); }, [params]);
  useEffect(() => {
    document.title = 'Learning Paths | Elevate Careers';
    fetch(`${API}/api/journey/public/learning-paths`).then((r) => r.json()).then(setPaths).catch(() => setPaths([]));
  }, []);
  const shown = paths.filter((p) => p.category === tab);
  return (
    <main data-testid="learning-paths-page">
      <section className="bg-[#0b1f4d] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Eyebrow>Learn by doing</Eyebrow>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl">Role-based learning paths, built on real work.</h1>
          <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed">Each path starts with a quick starter check, moves through practical tasks modelled on what employers actually check, and ends with a portfolio project. Final-year students and experienced professionals get separate tracks, and every task is reviewed by an Elevate consultant. Your consultant helps you pick the right path after your Career Clarity Consultation.</p>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <TealButton to="/network" testid="learning-start-btn">Start My Career Journey</TealButton>
            <Link to="/programs" className="text-sm font-semibold text-teal-300 hover:text-teal-200" data-testid="learning-programs-link">See programs & pricing</Link>
          </div>
        </div>
      </section>
      <section className="bg-slate-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
          <div className="inline-flex bg-white border border-slate-200 rounded-full p-1 mb-8" data-testid="learning-tabs">
            {[['technical', 'Technology & Data'], ['nontechnical', 'Business, Sales & People']].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`learning-tab-${k}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === k ? 'bg-[#0b1f4d] text-white' : 'text-slate-600 hover:text-[#0b1f4d]'}`}>{l}</button>
            ))}
          </div>
          {!paths.length ? <div className="h-40 rounded-2xl bg-white animate-pulse" /> : (
            <div className="space-y-4">{shown.map((p) => <PathCard key={p.id} p={p} open={open === p.id} onToggle={() => setOpen(open === p.id ? '' : p.id)} track={track} setTrack={setTrack} />)}</div>
          )}
        </div>
      </section>
    </main>
  );
}
